/**
 * SwipeTabPanel — 탭·칩으로 갈아 끼우는 내용 영역에 **가로 스와이프 전환 + 슬라이드 연출**을
 * 얹는 공통 조각.
 *
 * 탭 헤더 모양은 화면마다 다르다(ToggleButton 칩 · SegmentedControl · TabList). 그래서
 * 헤더는 각 화면이 그리고, 이 컴포넌트는 그 아래 **내용 면**만 감싼다.
 *   - 스와이프: 왼쪽으로 밀면 다음, 오른쪽으로 밀면 이전 탭(`tabs` 순서). 판정·클릭 억제는
 *     `useSwipeTabs` 가 맡는다.
 *   - 연출: 탭이 바뀌면(밀든 누르든) 새 내용이 **가려는 방향에서** 밀려 들어온다
 *     (템플릿 SwipeTabs 와 같은 36px 슬라이드 + 페이드). 데이터만 바뀌면 넘어갔는지가
 *     안 보인다는 제보(2026-09-07)로 넣었다. `key={activeTab}` 이라 내용은 탭마다 remount 된다.
 *
 * ⚠ 감싸는 범위를 고른다: 가로 스크롤(캐러셀·넘치는 칩 줄)이 든 영역을 감싸면 스크롤과
 *   전환이 함께 일어난다. 홈은 §2 목록만, 알림은 필터 줄을 뺀 목록만 감싼 이유다.
 * ⚠ `touch-action: pan-y` — 세로 스크롤은 브라우저가 계속 맡고, 가로 이동은 브라우저가
 *   pointercancel 로 빼앗지 않게 한다(이게 없으면 Android WebView 에서 판정 전에 끊긴다).
 *   `overflow: hidden` 은 슬라이드 중 x 오프셋이 가로 스크롤을 만들지 않게 한다.
 */
import { useRef, type ReactNode } from "react";
import { motion } from "framer-motion";
import { useSwipeTabs } from "../../hooks/useSwipeTabs";

interface SwipeTabPanelProps<T extends string> {
  tabs: readonly T[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  children: ReactNode;
  className?: string;
}

export default function SwipeTabPanel<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  children,
  className,
}: SwipeTabPanelProps<T>) {
  const handlers = useSwipeTabs(tabs, activeTab, onTabChange);

  // 방향은 "직전 탭 → 지금 탭" 의 인덱스 차로 정한다. 누른 칩이든 스와이프든 같은 규칙.
  const prevTabRef = useRef(activeTab);
  const directionRef = useRef(1);
  if (prevTabRef.current !== activeTab) {
    const from = tabs.indexOf(prevTabRef.current);
    const to = tabs.indexOf(activeTab);
    directionRef.current = to >= from ? 1 : -1;
    prevTabRef.current = activeTab;
  }

  return (
    <div
      className={className}
      style={{ touchAction: "pan-y", overflow: "hidden" }}
      data-swipe-tabs=""
      {...handlers}
    >
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: directionRef.current * 36 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}
